import Log from "utils/log";
import { APIError } from "@axioscode/api-client/lib/utils/errors";

/**
 * A convenience function to handle the majority of API action requests that are
 * processed through redux. Dispatches REQUESTED, SUCCEEDED, and FAILED methods
 * of a predictable form.
 *
 * @param {string} resource The API resource to query
 * @param {string} method The API method to query
 * @param {string} actionName The base name of the actions to be dispatched.
 * @param {function} getCallArgs Given the arguments passed to the action,
 *   return an object to be passed to the API client.
 * @param {function} errFn Extra code to run after the actionFailed method is dispatched
 */
export default function(
  resource,
  method,
  actionName,
  getCallArgs = args => args,
  errFn
) {
  // Scaffold out the action dispatchers.
  const getActionRequested = () => ({ type: `${actionName}_REQUESTED` });
  const getActionSucceeded = payload => ({
    type: `${actionName}_SUCCEEDED`,
    payload,
  });
  const getActionFailed = payload => {
    const actionNameType = `${actionName}_FAILED`;
    const fingerprints = [actionNameType];
    if (payload && payload.message) {
      fingerprints.push(payload.message);
    }

    if (payload instanceof APIError) {
      // APIErrors get their classnames mangled by minification. Helpfully set the name of the error
      // to the type of the action that failed.
      payload.name = actionNameType;
    }

    Log.logger(payload, "error", undefined, true, fingerprints);
    return {
      type: actionNameType,
      error: true,
      payload,
    };
  };

  // The actual thunk action.
  return (...args) => (dispatch, getState, getClient) => {
    const api = getClient(dispatch, getState);

    dispatch(getActionRequested());

    // Check to see if the processed call args are an array and wrap them if they're not.
    const callArgs = getCallArgs(args, getState);
    const wrappedCallArgs = Array.isArray(callArgs) ? callArgs : [callArgs];

    return api[resource][method](...wrappedCallArgs)
      .then(payload => dispatch(getActionSucceeded(payload)))
      .catch(error => {
        const dispatchResult = dispatch(getActionFailed(error));
        return errFn ? errFn(error) : dispatchResult;
      });
  };
}
